import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { useFormContext } from 'react-hook-form';
import AICalendar from './AICalendar';
import { useGetIndividualAssetQuery } from '../../../../app/Slices/SevenDayApiSlice';
import GenericTable from '../../../shared/GenericTable';
import { MRT_ColumnDef } from 'mantine-react-table';

const AvailabilityTable = ({
    availabilityResponse,
    handleSubmitMain,
    charges,
    setCharges,
    isSmallChat,
    bookingInfo,
    moduleId,
    disableSubmit,
}: {
    availabilityResponse: any;
    handleSubmitMain: any;
    charges: any;
    setCharges: any;
    isSmallChat: any;
    bookingInfo: any;
    moduleId: number;
    disableSubmit: boolean;
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [dates, setDates] = useState(availabilityResponse.availability);

    const theme = useTheme();

    const { data: experience, isLoading: experienceLoading } =
        useGetIndividualAssetQuery(`/modules/${moduleId}`, {
            skip: !moduleId,
        });

    const { setValue } = useFormContext();
    const handleSubmit = () => {
        const datesToBeSubmitted = dates
            .filter((date: any) => date.isAvailable)
            .map(
                (date: any) =>
                    `{ startDate: ${date.startDate}, endDate: ${date.endDate} }`,
            );

        const message = `Reserve these dates: [ ${datesToBeSubmitted.join(
            ', ',
        )} ]`;

        setValue('outgoingMessage', message);
        setValue('renderMessage', false);
        setValue('disableSendButton', true);

        handleSubmitMain();
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'startDate',
            header: 'Start Date',
            size: 100,
            Cell: ({ cell, row }) => (
                <span
                    style={{
                        color:
                            row.original?.isAvailable === false
                                ? 'red'
                                : 'inherit',
                    }}
                >
                    {cell.getValue<string>()}
                </span>
            ),
        },
        {
            accessorKey: 'endDate',
            header: 'End Date',
            size: 100,
            Cell: ({ cell, row }) => (
                <span
                    style={{
                        color:
                            row.original?.isAvailable === false
                                ? 'red'
                                : 'inherit',
                    }}
                >
                    {cell.getValue<string>()}
                </span>
            ),
        },
        {
            accessorKey: 'isAvailable',
            header: 'Availability',
            size: 100,
            Cell: ({ cell, row }) => {
                const value = cell.getValue();
                return (
                    <span style={{ color: value ? 'inherit' : 'red' }}>
                        {value ? 'Available' : 'Unavailable'}
                    </span>
                );
            },
        },
        {
            accessorKey: 'reason',
            header: 'Reason',
            size: 200,
            Cell: ({ cell, row }) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        color:
                            row.original?.isAvailable === false
                                ? 'red'
                                : 'inherit',
                    }}
                >
                    {cell.getValue<string>()}
                </div>
            ),
        },

        {
            id: 'action',
            header: '',
            size: 50,
            Cell: ({ cell, row }) => {
                const onClick = (e: any) => {
                    const currentRowId = row?.original.id;

                    const removeReservation = dates.filter((value: any) => {
                        return value?.id !== currentRowId;
                    });

                    setDates(removeReservation);
                };

                return (
                    <CustomIconButton
                        sx={{
                            backgroundColor: 'transparent',
                            width: '25px',
                            height: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'primary.main',

                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        onClick={onClick}
                    >
                        <DeleteForeverIcon
                            fontSize='small'
                            sx={{ fontSize: '16px' }}
                        />
                    </CustomIconButton>
                );
            },
        },
    ];

    const memoizedData = useMemo(() => {
        if (!dates) return [];
        return dates.map((row: any) => ({
            ...row,
        }));
    }, [dates]);

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile || isSmallChat ? 'column' : 'row',
                gap: 5,
                justifyContent: 'flex-end',
            }}
        >
            {experience && !disableSubmit && (
                <AICalendar
                    setCharges={setCharges}
                    charges={charges}
                    experience={experience}
                    handleSubmitMain={() => {
                        return;
                    }}
                    disableSendButton={false}
                    isSmallChat={isSmallChat}
                    component={'availabilityTable'}
                    setDates={setDates}
                    bookingInfo={bookingInfo}
                />
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'auto',
                }}
            >
                <GenericTable
                    columns={columns}
                    data={memoizedData}
                    tableName='Availability'
                    loading={!dates}
                    enableBottomToolbar={false}
                    enableTopToolbar={false}
                    maxHeight={'370px'}
                    enableSorting={false}
                    enableColumnActions={false}
                    enableRowSelection={false}
                />
                <Grid
                    sx={{
                        alignSelf: 'flex-end',
                    }}
                >
                    <CustomButton
                        size='small'
                        color='primary'
                        variant='contained'
                        onClick={handleSubmit}
                        disableElevation
                        disableRipple
                        disabled={
                            dates
                                ?.map((date: any) => date.isAvailable)
                                .filter((item: boolean) => item).length === 0 ||
                            disableSubmit
                        }
                        sx={{
                            height: '100%',
                            display: 'flex',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.textAccent.contrastText,
                                backgroundColor: theme.palette.textAccent.main,
                            },
                        }}
                    >
                        Submit
                    </CustomButton>
                </Grid>
            </Box>
        </Box>
    );
};

export default AvailabilityTable;
